import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
// @ts-ignore
import 'react-datepicker/dist/react-datepicker.css';

interface DateInputProps {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  placeholder = 'DD/MM/YYYY',
  required = false,
  className = '',
  disabled = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [displayValue, setDisplayValue] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  // Convert storage format (yyyy-mm-dd) to display format (dd/mm/yyyy)
  const toDisplayFormat = (storageDate: string): string => {
    if (!storageDate) return '';
    const parts = storageDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return storageDate;
  };

  // Convert display format (dd/mm/yyyy) to storage format (yyyy-mm-dd)
  const toStorageFormat = (displayDate: string): string => {
    if (!displayDate) return '';
    const parts = displayDate.split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return '';
  };

  // Convert display format to Date object for calendar
  const toDateObject = (displayDate: string): Date | null => {
    if (!displayDate) return null;
    const parts = displayDate.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const year = parseInt(parts[2]);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    return null;
  };

  // Handle manual input
  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Get only digits
    const digits = inputValue.replace(/\D/g, '');
    
    let formatted = '';
    
    if (digits.length >= 1) {
      formatted = digits.substring(0, 2);
      if (digits.length >= 3) {
        formatted = digits.substring(0, 2) + '/' + digits.substring(2, 4);
      }
      if (digits.length >= 5) {
        formatted = digits.substring(0, 2) + '/' + digits.substring(2, 4) + '/' + digits.substring(4, 8);
      }
    }
    
    setDisplayValue(formatted);
    
    // Save if complete
    if (formatted.length === 10 && formatted.includes('/')) {
      const storageDate = toStorageFormat(formatted);
      if (storageDate) {
        onChange(storageDate);
        setSelectedDate(toDateObject(formatted));
      }
    } else if (formatted.length === 0) {
      onChange('');
      setSelectedDate(null);
    }
  };

  // Handle date selection from calendar
  const handleCalendarChange = (date: Date | null) => {
    if (date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const displayDate = `${day}/${month}/${year}`;
      const storageDate = `${year}-${month}-${day}`;
      
      setDisplayValue(displayDate);
      setSelectedDate(date);
      onChange(storageDate);
    }
    setIsCalendarOpen(false);
  };

  // Toggle calendar
  const toggleCalendar = () => {
    if (!disabled) {
      setIsCalendarOpen(!isCalendarOpen);
    }
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarContainerRef.current && !calendarContainerRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  // Update when external value changes
  useEffect(() => {
    const newDisplayValue = toDisplayFormat(value);
    setDisplayValue(newDisplayValue);
    setSelectedDate(toDateObject(newDisplayValue));
  }, [value]);

  // Get calendar position
  const getCalendarPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const calendarHeight = 350; // Approximate height of the calendar
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      
      // If there's more space above, position the calendar above the input
      if (spaceAbove > spaceBelow) {
        return {
          top: rect.top + window.scrollY - calendarHeight - 5,
          left: rect.left + window.scrollX,
          positionAbove: true
        };
      } else {
        // Otherwise position below
        return {
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX,
          positionAbove: false
        };
      }
    }
    return { top: 0, left: 0, positionAbove: false };
  };

  const calendarPosition = getCalendarPosition();

  return (
    <div className="relative w-full" ref={calendarContainerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={className}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          value={displayValue}
          onChange={handleManualChange}
        />
        <button
          type="button"
          onClick={toggleCalendar}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
          disabled={disabled}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-5 w-5" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            strokeWidth={1.5}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M12 12.75h.008v.008H12v-.008zM15 12.75h.008v.008H15v-.008zM9 12.75h.008v.008H9v-.008zM12 16.5h.008v.008H12V16.5zM15 16.5h.008v.008H15V16.5zM9 16.5h.008v.008H9V16.5z" 
            />
          </svg>
        </button>
      </div>
      
      {/* Custom Calendar Popup */}
      {isCalendarOpen && (
        <div 
          className="fixed z-[99999] bg-white rounded-lg shadow-lg border border-gray-200 p-2"
          style={{
            top: `${calendarPosition.top}px`,
            left: `${calendarPosition.left}px`,
            transformOrigin: calendarPosition.positionAbove ? 'bottom center' : 'top center'
          }}
        >
          {/* Optional: Add a small arrow indicator */}
          <div 
            className={`absolute ${calendarPosition.positionAbove ? 'bottom-0 mb-[-8px]' : 'top-0 mt-[-8px]'} left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-t border-l border-gray-200 rotate-45`}
            style={{
              borderColor: calendarPosition.positionAbove ? 'transparent transparent #e5e7eb #e5e7eb' : '#e5e7eb #e5e7eb transparent transparent'
            }}
          ></div>
          <DatePicker
            selected={selectedDate}
            onChange={handleCalendarChange}
            dateFormat="dd/MM/yyyy"
            inline
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
          />
        </div>
      )}

    </div>
  );
};

export default DateInput;